<template lang="pug">
  .b-project-detail
    ._container
      ._image-wrap
        AvatarEditable._image(:avatar="project.avatar" :editable="false" placeholder="no-avatar-project")
      ._left
        h1._title {{ project.name }}
          ._label.-green(v-if="access.isProjectAuthor()") Мой проект
          ._label.-yellow(v-if="project.isReferralSample") Авторский проект
        address._address(v-if="project.objectAddress")
          Icon(name="map")
          | {{ project.objectAddress }}
        ._progress-container
          BaseProgressbar._progressbar(styling="header" v-bind="dateProgress" :color="progressbarColor")
          ._progress-tooltip(v-if="dateProgress.label") {{ dateProgress.label }}
      ._right
        ._menu
          Bookmark(light category="project" :id="project['@id']")
          EllipsisMenu(light :items="ellipsisMenuItems")
        ._params
          ._param(v-for="param in params" :key="param.desc")
            ._param-image
              Icon(:name="param.icon")
            ._param-text
              ._param-value(v-html="param.value")
              ._param-desc(v-html="param.desc")
        ._button.project-detail-button
</template>

<script>
import Access from '@/utils/access';
import dateProgress from '@/utils/dateProgress';
import projectModel from '@/model/project';

import BaseProgressbar from '@/components/BaseProgressbar.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import AvatarEditable from '@/components/AvatarEditable.vue';

export default {
  name: 'ProjectDetail',
  components: { AvatarEditable, EllipsisMenu, BaseProgressbar }, // eslint-disable-line
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
    };
  },
  computed: {
    access() { return new Access(this.project, this.$store.state.auth.user); },
    dateProgress() { return dateProgress(this.project.dateStart, this.project.dateFinish, this.project.archivedAt); },
    progressbarColor() {
      if (this.project.isArchived) {
        return 'grey';
      }

      if (!this.project.dateFinish) {
        return '';
      }

      const today = new Date((new Date()).setHours(0, 0, 0, 0));
      const finish = new Date((new Date(this.project.dateFinish)).setHours(0, 0, 0, 0));
      const diffInDays = Math.ceil((finish - today) / (24 * 60 * 60 * 1000));

      if (diffInDays < 1) {
        return 'red';
      }

      if (diffInDays < 3) {
        return 'orange';
      }

      if (diffInDays < 7) {
        return 'yellow';
      }

      return '';
    },
    avatar() { return this.project.avatar?.thumbnails.avatar_large ?? '/img/avatar.png'; },
    params() {
      const area = this.project.objectArea;
      const price = this.project.pricePerSquareMeter;
      const cost = price ? (price * area).toFixed(0) : null;

      const items = [
        { icon: 'square', desc: 'площадь', value: area ? `${area} м<sup>2</sup>` : '— м<sup>2</sup>' },
        { icon: 'purse', desc: 'цена за м<sup>2</sup>', value: price ? `${price} ₽` : '— ₽' },
        { icon: 'cost', desc: 'стоимость', value: cost ? `${cost} ₽` : '— ₽' },
      ];

      return items.filter((item) => item.value);
    },
    ellipsisMenuItems() {
      const { access, project } = this;

      const onArchiveStateChanged = () => { this.$store.commit('project/setProject', { ...this.project, isArchived: !this.project.isArchived }); };
      const onRemoved = () => { this.$router.push({ name: 'project-list-archived' }); };

      return projectModel.getEllipsisMenu({
        access,
        project,
        onArchiveStateChanged,
        onRemoved,
        onProjectLeft: () => {},
      });
    },
  },
};
</script>

<style scoped lang="scss">
.project-detail {
  padding-bottom: 84px;
  position: relative;

  &__container {
    display: grid;
    color: var(--color-white);
    gap: 40px;
    grid-template-columns: 176px repeat(10, 1fr);
    max-width: var(--container-size);
    margin: -20px auto 0;
    padding-left: var(--container-gutter);
    padding-right: var(--container-gutter);
  }
  &__image-wrap {
    display: block;
    grid-column: 1 span;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    align-self: start;

    &:before {
      display: block;
      content: '';
      width: 0;
      padding-bottom: 100%;
    }
  }
  &__image {
    display: block;
    box-sizing: border-box;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  &__left {
    grid-column: 5 span;
    display: flex;
    flex-direction: column;
    gap: 19px;
    padding: 8px;
  }
  &__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  &__label {
    font-size: 12px;
    display: flex;
    align-items: center;
    text-align: center;
    letter-spacing: 0.03em;
    color: #FFFFFF;
    border-radius: 4px;
    padding: 0 14px;
    box-sizing: border-box;
    height: 20px;
    position: relative;
    top: 2px;

    &--green { background: #5AC169; }
    &--yellow { background: #F9D208; }
  }
  &__address {
    display: flex;
    font-style: normal;
    font-size: 14px;

    svg {
      display: block;
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      padding-right: 4px;
      fill: var(--color-white);
    }
  }

  &__right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    grid-column: 5 span;
    padding-top: 17px;
  }

  &__menu {
    display: flex;
    align-items: center;
    gap: 34px;
    position: absolute;
    top: -35px;
  }

  &__params {
    display: flex;
    align-items: center;
    gap: 60px;
    justify-content: space-between;
  }

  &__param {
    display: flex;
    align-items: center;
  }
  &__param-text {
    display: flex;
    flex-direction: column;
    margin-left: 10px;
  }
  &__param-value {
    white-space: nowrap;
    font-weight: 500;
    font-size: 16px;
  }
  &__param-desc {
    white-space: nowrap;
    font-size: 12px;
    letter-spacing: 0.01em;
    font-weight: 500;
    color: rgba(#fff, 0.8);
  }
  &__param-image {
    padding: 11px;
    border-radius: 5px;
    background-color: var(--color-white);
    svg {
      display: block;
      width: 18px;
      height: 18px;
    }
  }

  &__progress-tooltip {
    position: absolute;
    z-index: 50;
    top: 30px;
    left: 50%;
    opacity: 0;
    padding: 8px 20px;
    visibility: hidden;
    transform: translateX(-50%);
    background-color: var(--color-white);
    color: var(--color-middle-gray);
    border-radius: 6px;
    box-shadow: 0 2px 30px rgba(0, 0, 0, 0.12);
    font-size: 14px;
    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
    &:before {
      content: "";
      display: inline-block;
      position: absolute;
      top: -16px;
      left: 50%;
      width: 32px;
      height: 16px;
      transform: translateX(-50%);
      background-image: url(../assets/img/svg/triangle.svg);
      background-size: cover;
      background-repeat: no-repeat;
    }
  }
  &__progress-container {
    margin-top: auto;
    position: relative;
    top: 5px;
  }
  &__progress-container:hover &__progress-tooltip {
    visibility: visible;
    opacity: 1;
  }

  @media (max-width: 1899.98px) {
    &__menu {
      top: -51px;
    }

    &__param-image {
      padding: 7px;
      border-radius: 7px;
    }
  }

  @media (max-width: 1880px) {
    &__menu {
      margin-right: calc(1508px - 80vw);
    }
  }

  @media (max-width: 1700px) {
    &__menu {
      margin-right: 120px;
    }
  }

  @media (max-width: 1366px) {
    &__menu {
      top: -59px;
      margin-right: 135px;
    }
  }

  @media (max-width: 1200px) {
    &__menu {
      right: 0;
      margin-right: 173px;
    }
  }

  @media (max-width: 768px) {
    &__menu {
      display: none;
    }
  }

  @media (max-width: 2300px) {
    &__container {
      margin-left:20vw;
    }
  }

  @media (max-width: 1700px) {
    &__container {
      margin-left: 0;
      max-width: none;
      padding: 0 40px;
      gap: 40px;
      grid-template-columns: 160px repeat(10, 1fr);
    }

    &__left {
      padding-left: 0;
    }
  }

  @media (max-width: 1200px) {
    padding-bottom: 48px;

    &__container {
      row-gap: 0;
    }
    &__image-wrap {
      grid-row: 2 span;
    }

    &__left {
      grid-column: 2 / 10 span;
    }

    &__right {
      grid-column: 2 / 10 span;
      flex-direction: row;
      align-items: center;
    }
  }

  @media (max-width: 1024px) {
    &__container {
      grid-template-columns: 95px repeat(11, 1fr);
      padding: 0 var(--container-gutter);
      gap:20px;
    }

    &__image-wrap {
      grid-column: 1 span;
    }

    &__left, &__right  {
      grid-column: 2 / 11 span;
    }
  }

  @media (max-width: 768px) {
    &__container {
      column-gap: 40px;
      row-gap: 20px;
      grid-template-columns: 136px 1fr;
      align-items: center;
    }

    &__image-wrap {
      grid-column: 1 span;
      grid-row: 1 span;
      align-self: center;
    }

    &__left {
      grid-column: 1 span;
    }

    &__address {
      display: none;
    }

    &__right  {
      grid-column: 2 span;
      flex-direction: column;
      align-items: center;
    }

    &__params {
      margin-bottom: 20px;
      width: 100%;
      justify-content: space-around;
      padding-bottom: 50px
    }

    &__param {
      flex-direction: column;
      margin: 0;
      position: relative;
    }

    &__param-text {
      margin: 0;
      position: absolute;
      text-align: center;
      top: calc(100% + 10px)
    }
  }

  @media (max-width: 576px) {
    &__container {
      margin-top: -50px;
      grid-template-columns:1fr 112px 1fr;
    }

    &__image-wrap {
      grid-column: 2 / 1 span;
    }

    &__left, &__right {
      grid-column: 3 span;
    }

    &__title {
      text-align: center;
    }
  }
}
</style>
